import Badge from "./ds/Badge";
import { STATUS_TONES } from "./discover/campaignDisplay";
import { useNavigate } from "react-router-dom";
import { FaTiktok, FaInstagram, FaFacebookF, FaYoutube } from "react-icons/fa";
import { FiArrowRight } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import ProfileUserLink from "./ProfileUserLink";
import { formatCampaignMoney } from "../utils/campaignCardUtils";

const CampaignAnnouncementCard = ({ campaign, isDark, mintSpaceId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  if (!campaign) return null;

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  
  const getPlatformIcons = (platforms) => {
    const icons = [];
    if (platforms.tiktok) icons.push(<FaTiktok key="tiktok" className="h-4 w-4" />);
    if (platforms.instagramReels) icons.push(<FaInstagram key="instagram" className="h-4 w-4" />);
    if (platforms.facebookReels) icons.push(<FaFacebookF key="facebook" className="h-4 w-4" />);
    if (platforms.youtubeShorts || platforms.youtubeLongForm) icons.push(<FaYoutube key="youtube" className="h-4 w-4" />);
    return icons;
  };

  const getParticipantTypeLabel = (type) => {
    switch (type) {
      case "creators":
        return t("campaigns.ugc");
      case "clippers":
        return t("campaigns.clipping");
      case "both":
        return t("campaigns.ugcAndClipping");
      default:
        return t("campaigns.ugc");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No end date";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const getStatusBadge = (status) => {

    const statusLabel =
      t(`common.status${status.charAt(0).toUpperCase() + status.slice(1)}`) ||
      status.charAt(0).toUpperCase() + status.slice(1);

    return (
      <Badge tone={STATUS_TONES[status] || "warning"}>{statusLabel}</Badge>
    );
  };

  const handleClick = () => {
    if (mintSpaceId) {
      navigate(`/mint-space/${mintSpaceId}?campaign=${campaign._id}&nav=campaigns`);
    } else {
      navigate(`/campaigns/${campaign._id}`);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`w-full rounded-radius-lg overflow-hidden transition-all duration-300 cursor-pointer group bg-bg-raised`}
    >
      <div className="flex flex-col gap-space-16 p-space-16 min-[600px]:gap-6 min-[600px]:p-8">
        {/* Header: Image, Title, Badges */}
        <div className="flex items-start gap-space-12 min-[600px]:gap-6">
          {/* Campaign Image */}
          <div className="relative size-16 overflow-hidden rounded-radius-md shrink-0 shadow-lg min-[600px]:size-32">
            {campaign.campaignLogo ? (
              <img
                src={campaign.campaignLogo}
                alt={campaign.campaignName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className={`w-full h-full flex items-center justify-center bg-accent-default`}>
                <span className={`text-4xl font-bold text-accent-on`}>
                  {campaign.campaignName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>

          {/* Title and Badges */}
          <div className="flex-1 min-w-0">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
              <h3 className={`min-w-0 break-words text-title-2 min-[600px]:text-title-1 ${baseText} group-hover:text-accent-text transition`}>
                {campaign.campaignName}
              </h3>
              <div className="flex shrink-0 items-center gap-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-footnote font-medium ${
                  "bg-accent-tinted text-accent-text border border-accent-default"
                }`}>
                  {getParticipantTypeLabel(campaign.participantType)}
                </span>
                {getStatusBadge(campaign.status)}
              </div>
            </div>
            
            {campaign.projectDescription && (
              <p className={`text-subheadline ${mutedText} line-clamp-2 mb-3`}>
                {campaign.projectDescription}
              </p>
            )}

            {campaign.createdBy && (
              <div className="mb-3" onClick={(e) => e.stopPropagation()}>
                <ProfileUserLink
                  user={campaign.createdBy}
                  fallback={t("common.user")}
                  className={baseText}
                  nameClassName={`text-subheadline ${baseText}`}
                  isDark={isDark}
                  stopPropagation
                />
              </div>
            )}

            <div className="flex items-center gap-4 text-footnote">
              <span className={mutedText}>
                {formatDate(campaign.startDate)}
              </span>
              <div className="flex gap-1.5 items-center">
                {getPlatformIcons(campaign.platforms)}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
          <div className="flex flex-col">
            <span className={`text-footnote font-medium ${mutedText} mb-1`}>Budget</span>
            <span className={`text-title-3 ${baseText}`}>
              {formatCampaignMoney(campaign.budget || 0)} ₾
            </span>
          </div>
          <div className="flex flex-col">
            <span className={`text-footnote font-medium ${mutedText} mb-1`}>Rate</span>
            <span className={`text-title-3 ${baseText}`}>
              {campaign.cpmRate} ₾ / 1000 views
            </span>
          </div>
          <div className="flex flex-col">
            <span className={`text-footnote font-medium ${mutedText} mb-1`}>Spent</span>
            <span className={`text-title-3 ${baseText}`}>
              {formatCampaignMoney(campaign.spent || 0)} ₾
            </span>
          </div>
        </div>

        {/* CTA Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
          className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-radius-sm bg-accent-default text-accent-on font-semibold transition shadow-lg group-`}
        >
          View Campaign Details
          <FiArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>
  );
};

export default CampaignAnnouncementCard;


